"use client";

import { useEffect, useRef, useState, useCallback } from "react";

declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
  }
}

type PlayerState = "unstarted" | "playing" | "paused" | "ended" | "buffering";

interface UseYouTubePlayerOptions {
  containerId: string;
  videoId: string;
  startMuted?: boolean;
  onEnded?: () => void;
}

export function useYouTubePlayer({
  containerId,
  videoId,
  startMuted = false,
  onEnded,
}: UseYouTubePlayerOptions) {
  const playerRef  = useRef<YT.Player | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Persiste o estado de mudo entre trocas de faixa
  const mutedRef = useRef(startMuted);
  const firstInteractionRef = useRef(false);
  const [state, setState]       = useState<PlayerState>("unstarted");
  const [muted, setMuted]       = useState(startMuted);
  const [apiReady, setApiReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]       = useState(0);

  // Carrega a API uma única vez
  useEffect(() => {
    if (window.YT?.Player) { setApiReady(true); return; }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => setApiReady(true);
  }, []);

  // Cria/recria o player quando videoId muda
  useEffect(() => {
    if (!apiReady || !videoId) return;
    playerRef.current?.destroy();
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCurrentTime(0);
    setDuration(0);

    playerRef.current = new window.YT.Player(containerId, {
      videoId,
      width: "100%",
      height: "100%",
      playerVars: {
        autoplay: 1,
        mute: mutedRef.current ? 1 : 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
        iv_load_policy: 3,
        playsinline: 1,
        enablejsapi: 1,
      },
      events: {
        onReady: (e) => {
          // Tenta autoplay - pode ser bloqueado no mobile até interação do usuário
          try {
            e.target.playVideo();
          } catch {
            // Autoplay bloqueado — aguardando interação do usuário
          }
          
          if (mutedRef.current) {
            e.target.mute();
          } else {
            e.target.unMute();
            e.target.setVolume(50);
          }
          setState("unstarted");
        },
        onStateChange: (e) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            setState("playing");
            setDuration(playerRef.current?.getDuration() ?? 0);
            intervalRef.current = setInterval(() => {
              setCurrentTime(playerRef.current?.getCurrentTime() ?? 0);
              setDuration(playerRef.current?.getDuration() ?? 0);
            }, 500);
          }
          if (e.data === window.YT.PlayerState.PAUSED) {
            setState("paused");
            if (intervalRef.current) clearInterval(intervalRef.current);
          }
          if (e.data === window.YT.PlayerState.BUFFERING) setState("buffering");
          if (e.data === window.YT.PlayerState.ENDED) {
            setState("ended");
            if (intervalRef.current) clearInterval(intervalRef.current);
            onEnded?.();
          }
        },
      },
    });

    return () => {
      playerRef.current?.destroy();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiReady, videoId, containerId]);

  // Detecta primeira interação do usuário para iniciar player no mobile
  useEffect(() => {
    const handleFirstInteraction = () => {
      if (!firstInteractionRef.current && playerRef.current && state === "unstarted") {
        firstInteractionRef.current = true;
        playerRef.current.playVideo();
      }
    };

    // Escuta eventos de toque e clique
    document.addEventListener('touchstart', handleFirstInteraction, { once: true });
    document.addEventListener('click', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('touchstart', handleFirstInteraction);
      document.removeEventListener('click', handleFirstInteraction);
    };
  }, [state]);

  const play  = useCallback(() => {
    try {
      playerRef.current?.playVideo();
    } catch {
      // Play bloqueado pelo navegador
    }
  }, []);
  const pause = useCallback(() => playerRef.current?.pauseVideo(), []);
  const togglePlay = useCallback(() => {
    if (state === "playing") pause(); else play();
  }, [state, play, pause]);

  const seek = useCallback((s: number) => {
    playerRef.current?.seekTo(s, true);
  }, []);

  const toggleMute = useCallback(() => {
    if (!playerRef.current) return;
    if (mutedRef.current) {
      playerRef.current.unMute();
      playerRef.current.setVolume(50);
      mutedRef.current = false;
      setMuted(false);
    } else {
      playerRef.current.mute();
      mutedRef.current = true;
      setMuted(true);
    }
  }, []);

  return { state, muted, currentTime, duration, togglePlay, play, pause, seek, toggleMute };
}
